/**
 * Enhanced LocalBusiness Schema with GBP alignment
 * Links website entity to Google Business Profile for Knowledge Panel
 * Includes sameAs, hasMap, and review action markup
 */

import { useEffect } from "react";

const GBP_PLACE_ID = "ChIJGRnb8Y4hEW_aXz-ZmPk8nFM";
const GBP_MAP_URL = "https://www.google.com/maps/place/California+Energy+Advisor+Group/";
const GBP_REVIEW_URL = `https://search.google.com/local/writereview?placeid=${GBP_PLACE_ID}`;

interface GBPLocalBusinessSchemaProps {
  /** Override Place ID when available */
  placeId?: string;
}

const GBPLocalBusinessSchema = ({ placeId }: GBPLocalBusinessSchemaProps = {}) => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://www.gosolar.pro/#organization",
      "name": "California Energy Group",
      "alternateName": "CEAG",
      "description":
        "California's trusted solar + battery storage advisor. $Zero down PPA. Tesla Powerwall, Enphase IQ, Franklin batteries. 25-year warranty. Serving all of California.",
      "url": "https://www.gosolar.pro",
      "telephone": "+1-661-663-0608",
      "email": "info@gosolar.pro",
      "image": "https://www.gosolar.pro/og-image.jpg",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.gosolar.pro/pwa-512x512.png",
        "width": 512,
        "height": 512,
      },
      "foundingDate": "2015",
      "founder": {
        "@type": "Person",
        "name": "Eric D. Gutierrez",
        "jobTitle": "Founder & Lead Energy Advisor",
        "hasCredential": [
          {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "Professional License",
            "name": "CSLB C-46 Solar Contractor License #1106249",
          },
          {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "Professional Certification",
            "name": "NABCEP Certified Solar Professional",
          },
        ],
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bakersfield",
        "addressRegion": "CA",
        "addressCountry": "US",
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 35.3733,
        "longitude": -119.0187,
      },
      "areaServed": {
        "@type": "State",
        "name": "California",
        "sameAs": "https://en.wikipedia.org/wiki/California",
      },
      "hasMap": GBP_MAP_URL,
      "sameAs": [
        GBP_MAP_URL,
        GBP_REVIEW_URL,
        "https://www.yelp.com/biz/california-energy-advisor-group-fresno",
        "https://www.bbb.org/us/ca/california-energy-advisor-group",
      ],
      "priceRange": "$ Zero (PPA)",
      "paymentAccepted": "Power Purchase Agreement, Solar Lease",
      "currenciesAccepted": "USD",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "08:00",
          "closes": "18:00",
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "09:00",
          "closes": "14:00",
        },
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "127",
        "bestRating": "5",
        "worstRating": "1",
      },
      "makesOffer": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Residential Solar + Battery Installation",
            "description": "$Zero down PPA with battery backup. 25-year warranty.",
          },
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "EV Charger Installation",
            "description": "Level 2 EV charging integrated with solar.",
          },
        },
      ],
      "knowsAbout": [
        "Solar Energy",
        "Battery Storage",
        "NEM 3.0",
        "Power Purchase Agreement",
        "Tesla Powerwall",
        "Enphase IQ Battery",
        "EV Charging",
      ],
      "potentialAction": {
        "@type": "ReserveAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://www.gosolar.pro/#schedule-savings",
          "actionPlatform": [
            "http://schema.org/DesktopWebPlatform",
            "http://schema.org/MobileWebPlatform",
          ],
        },
        "result": {
          "@type": "Reservation",
          "name": "Free Solar Consultation",
        },
      },
    };

    const scriptId = "gbp-local-business-schema";
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [placeId]);

  return null;
};

export default GBPLocalBusinessSchema;
